import { onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'dashboard/composables/store';

// Aceleres 2026-09-09: rede de segurança para a bolha de mídia que ainda não
// chegou. O caminho normal é o broadcast `message.updated`, e para o
// RESPONSÁVEL pela conversa ele funciona — medido em homologação, a bolha vira
// imagem sozinha em ~3 s. Mas o broadcast do Zoraxy é escopado ao responsável,
// então quem só acompanha a conversa não recebe evento nenhum e ficava preso no
// cartão "Entregando no atendimento" até recarregar a página. Aqui a bolha
// pergunta pela própria mensagem, de vez em quando, enquanto continuar pendente.
//
// Os tempos crescem de propósito: a mídia que vem depressa é apanhada na
// primeira tentativa e as seguintes nem chegam a correr (o componente
// desmonta). A que demora não pode custar um pedido a cada segundo.
const TENTATIVAS_MS = [4000, 9000, 20000, 45000, 90000];

// Um agendador por CONVERSA, não por bolha: uma conversa com dez mídias
// pendentes faria dez pedidos iguais, cada um a devolver as mesmas mensagens.
const agendadores = new Map();

const pararAgendador = conversationId => {
  const agendador = agendadores.get(conversationId);
  if (!agendador) return;
  window.clearTimeout(agendador.timer);
  agendadores.delete(conversationId);
};

const agendar = (store, conversationId) => {
  const agendador = agendadores.get(conversationId);
  if (!agendador || agendador.timer) return;

  const espera = TENTATIVAS_MS[agendador.tentativa];
  if (espera === undefined) {
    // Esgotou as tentativas. Não insistir para sempre: a partir daqui a bolha
    // fica no cartão até um evento real ou uma releitura da conversa — mas o
    // cartão diz o que está a acontecer, não é um retângulo vazio.
    pararAgendador(conversationId);
    return;
  }

  agendador.timer = window.setTimeout(async () => {
    const atual = agendadores.get(conversationId);
    if (!atual) return;
    atual.timer = null;
    atual.tentativa += 1;

    const resolvidas = await store.dispatch('refreshPendingMediaMessages', {
      conversationId,
      messageIds: [...atual.ids],
    });
    resolvidas.forEach(id => atual.ids.delete(id));

    if (!atual.ids.size) {
      pararAgendador(conversationId);
      return;
    }
    agendar(store, conversationId);
  }, espera);
};

/**
 * Enquanto esta bolha estiver montada, a mensagem dela é reconsultada de tempos
 * a tempos. Quando a mídia chega, o componente desmonta e a consulta pára
 * sozinha — não é preciso desligar nada à mão.
 *
 * @param {import('vue').Ref<number>} conversationId
 * @param {import('vue').Ref<number>} messageId
 * @param {import('vue').Ref<boolean>} ativo - falso quando já não há o que
 *   esperar (mídia declarada perdida), para não consultar o servidor à toa.
 */
export const usePendingMediaRecovery = (conversationId, messageId, ativo) => {
  const store = useStore();

  onMounted(() => {
    if (ativo && ativo.value === false) return;
    const conversa = Number(conversationId?.value);
    const mensagem = Number(messageId?.value);
    if (!Number.isFinite(conversa) || !Number.isFinite(mensagem)) return;

    if (!agendadores.has(conversa)) {
      agendadores.set(conversa, { ids: new Set(), tentativa: 0, timer: null });
    }
    const agendador = agendadores.get(conversa);
    agendador.ids.add(mensagem);
    // Uma bolha pendente NOVA reabre a contagem: o recuo existe para não
    // martelar o servidor pela mesma mídia, não para punir a próxima.
    agendador.tentativa = 0;
    agendar(store, conversa);
  });

  onBeforeUnmount(() => {
    const conversa = Number(conversationId?.value);
    const agendador = agendadores.get(conversa);
    if (!agendador) return;
    agendador.ids.delete(Number(messageId?.value));
    if (!agendador.ids.size) pararAgendador(conversa);
  });
};
